import { useEffect, useState } from 'react';
import { useI18n } from '../i18n/useI18n';
import { fetchStagePrefs, updateStagePrefs } from '../utils/profileApi';

interface Props {
  isOpen: boolean;
  userId: string;
  fallbackName: string;
  onClose: () => void;
  onSaved: (displayName: string) => void;
}

export default function StagePrefsModal({ isOpen, userId, fallbackName, onClose, onSaved }: Props) {
  const { t } = useI18n();
  const [name, setName] = useState('');
  const [showAvatar, setShowAvatar] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setLoaded(false);
    setError(null);
    fetchStagePrefs(userId).then((prefs) => {
      if (prefs) {
        setName(prefs.displayName);
        setShowAvatar(prefs.showAvatarOnStage);
      } else {
        setName('');
        setShowAvatar(true);
      }
      setLoaded(true);
    });
  }, [isOpen, userId]);

  if (!isOpen) return null;

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const trimmed = name.trim();
      await updateStagePrefs(userId, {
        displayName: trimmed || null,
        showAvatarOnStage: showAvatar,
      });
      onSaved(trimmed);
      onClose();
    } catch {
      setError(t('singer.stagePrefs.error'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card stage-prefs-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title">{t('singer.stagePrefs.title')}</h3>
          <button className="modal-close" onClick={onClose} aria-label={t('common.close')}>
            ✕
          </button>
        </div>
        <div className="modal-body">
          {!loaded ? (
            <p className="stage-prefs-loading">{t('common.loading')}</p>
          ) : (
            <>
              <label className="stage-prefs-label" htmlFor="stage-prefs-name">
                {t('singer.stagePrefs.displayName')}
              </label>
              <input
                id="stage-prefs-name"
                className="stage-prefs-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={fallbackName}
                maxLength={50}
              />
              <p className="stage-prefs-hint">{t('singer.stagePrefs.nameHint')}</p>

              <div className="stage-prefs-toggle-row">
                <div>
                  <div className="stage-prefs-toggle-label">{t('singer.stagePrefs.showAvatar')}</div>
                  <div className="stage-prefs-hint">{t('singer.stagePrefs.showAvatarHint')}</div>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={showAvatar}
                  aria-label={t('singer.stagePrefs.showAvatar')}
                  className={`stage-prefs-switch ${showAvatar ? 'stage-prefs-switch--on' : ''}`}
                  onClick={() => setShowAvatar((v) => !v)}
                >
                  <span className="stage-prefs-switch-knob" />
                </button>
              </div>

              {error && <p className="stage-prefs-error">{error}</p>}

              <button className="btn-primary stage-prefs-save" onClick={handleSave} disabled={saving}>
                {saving ? t('common.saving') : t('common.save')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
